import React, { createContext, useContext, useEffect, useRef, useState } from "react"
import type { Session, User } from "@supabase/supabase-js"

import { isMfaExempt } from "../config/mfaExemptions"
import { getMfaGateDecision, shouldRecheckMfaOnSignIn } from "./mfaGatePolicy"
import type { DevSession } from "./dev-session"
import { clearDevSession, getDevSession, isDevLoginEnabled, setDevSession } from "./dev-session"
import { supabase } from "./supabase"
import { trpcVanilla } from "./trpc"

/**
 * Forced-MFA gate state for the whole app:
 *  - "checking"  — still resolving AAL (show spinner)
 *  - "ok"        — aal2, passkey, exempt, or dev session → app is usable
 *  - "enroll"    — no TOTP factor → must enroll before using the app
 *  - "challenge" — has a factor but session is aal1 → must step up
 *  - "error"     — assurance check failed → retry before opening the app
 */
export type MfaStatus = "checking" | "ok" | "enroll" | "challenge" | "error"

const AAL_CHECK_TIMEOUT_MS = 4000
function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return Promise.race([p, new Promise<T>((_, reject) => setTimeout(() => reject(new Error("AAL check timeout")), ms))])
}

/** Adapt the slim dev user into a real Supabase `User` shape (no unsafe cast). */
function toSupabaseUser(u: DevSession["user"]): User {
  return {
    id: u.id,
    email: u.email,
    app_metadata: {},
    user_metadata: u.user_metadata ?? {},
    aud: "authenticated",
    created_at: "",
  }
}

/** Shape a persisted dev session into the Supabase `Session` the app expects. */
function devSessionToSupabase(dev: DevSession): Session {
  const nowSec = Math.floor(Date.now() / 1000)
  return {
    access_token: dev.accessToken,
    refresh_token: "dev",
    expires_in: Math.max(0, dev.expiresAt - nowSec),
    expires_at: dev.expiresAt,
    token_type: "bearer",
    user: toSupabaseUser(dev.user),
  }
}

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  mfaRequired: boolean
  currentAAL: string | null
  nextAAL: string | null
  signIn: (email: string, password: string) => Promise<{ error?: string }>
  signUp: (email: string, password: string, name: string) => Promise<{ error?: string }>
  signInWithGoogle: (redirectTo?: string) => Promise<{ error?: string }>
  /** Local-dev only: one-click login as any user (defaults to shray@aixlabs.co). */
  devLogin: (email?: string) => Promise<{ error?: string }>
  /** Whether the dev login path is available (only under `vite dev`). */
  isDevLoginEnabled: boolean
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<{ error?: string }>
  updatePassword: (password: string) => Promise<{ error?: string }>
  checkMFAStatus: () => Promise<void>
  /** App-wide forced-MFA gate state (drives the enrollment/challenge screen). */
  mfaStatus: MfaStatus
  /** Re-evaluate the MFA gate (call after a successful enroll/challenge verify). */
  recheckMfa: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
  /** Runs after sign-out clears the session; main.tsx drops the persisted query cache so the next user can't see it */
  onSignedOut?: () => void
}

export function AuthProvider({ children, onSignedOut }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [mfaRequired, setMfaRequired] = useState(false)
  const [currentAAL, setCurrentAAL] = useState<string | null>(null)
  const [nextAAL, setNextAAL] = useState<string | null>(null)
  const [mfaStatus, setMfaStatus] = useState<MfaStatus>("checking")
  const mfaCheckedUserIdRef = useRef<string | null>(null)

  /**
   * Resolve the forced-MFA gate. Reads a fresh session so it's not subject to
   * stale closure state. A failed check keeps protected routes closed until retry.
   */
  const checkMFAStatus = async () => {
    setMfaStatus("checking")
    try {
      // Local-dev one-click sessions can't do real MFA — never gate them.
      if (getDevSession()) {
        setMfaStatus("ok")
        return
      }

      const {
        data: { session: freshSession },
      } = await supabase.auth.getSession()

      // Not signed in → nothing to gate (ProtectedRoute handles the redirect).
      if (!freshSession?.user) {
        setMfaStatus("ok")
        return
      }
      mfaCheckedUserIdRef.current = freshSession.user.id

      // Service / automation accounts are exempt (can't do interactive TOTP).
      if (isMfaExempt(freshSession.user.email)) {
        setMfaStatus("ok")
        return
      }

      const { data, error } = await withTimeout(
        supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
        AAL_CHECK_TIMEOUT_MS,
      )
      if (error || !data) throw error ?? new Error("AAL check returned no data")

      setCurrentAAL(data.currentLevel)
      setNextAAL(data.nextLevel)
      const decision = getMfaGateDecision(data.currentLevel, data.nextLevel, data.currentAuthenticationMethods)
      setMfaRequired(decision.mfaRequired)
      setMfaStatus(decision.status)
    } catch (error) {
      console.warn("MFA gate check failed:", error)
      setMfaStatus("error")
    }
  }

  useEffect(() => {
    // Set a timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      setLoading(false)
    }, 3000) // 3 seconds timeout

    // Local-dev one-click login takes precedence over Supabase/Google.
    const dev = getDevSession()
    if (dev) {
      clearTimeout(timeoutId)
      setSession(devSessionToSupabase(dev))
      setUser(toSupabaseUser(dev.user))
      setMfaStatus("ok") // dev sessions bypass the forced-MFA gate
      setLoading(false)
      return () => clearTimeout(timeoutId)
    }

    // Get initial session
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        clearTimeout(timeoutId)
        setSession(session)
        setUser(session?.user ?? null)

        // Evaluate the forced-MFA gate for an already-signed-in user.
        if (session?.user) {
          void checkMFAStatus()
        } else {
          setMfaStatus("ok")
        }

        setLoading(false)
      })
      .catch((error) => {
        clearTimeout(timeoutId)
        console.error("❌ Auth initialization error:", error)
        setLoading(false) // Set loading to false even on error
      })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)

      // Re-evaluate the forced-MFA gate on sign-in (incl. OAuth callback). The
      // check is timeout-guarded, so it can't hang the callback.
      if (event === "SIGNED_IN" && session && shouldRecheckMfaOnSignIn(mfaCheckedUserIdRef.current, session.user.id)) {
        setMfaStatus("checking")
        void checkMFAStatus()
      }

      // Clear MFA state on sign out
      if (event === "SIGNED_OUT") {
        mfaCheckedUserIdRef.current = null
        setMfaRequired(false)
        setCurrentAAL(null)
        setNextAAL(null)
        setMfaStatus("ok")
      }
    })

    return () => {
      clearTimeout(timeoutId)
      subscription.unsubscribe()
    }
  }, [])

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        return { error: error.message }
      }

      return {}
    } catch {
      return { error: "An unexpected error occurred" }
    }
  }

  const signUp = async (email: string, password: string, name: string) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
          },
        },
      })

      if (error) {
        return { error: error.message }
      }

      return {}
    } catch {
      return { error: "An unexpected error occurred" }
    }
  }

  const signInWithGoogle = async (redirectTo?: string) => {
    try {
      const callbackUrl = new URL("/auth/callback", window.location.origin)
      if (redirectTo) {
        callbackUrl.searchParams.set("redirect", redirectTo)
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: callbackUrl.toString(),
        },
      })

      if (error) {
        return { error: error.message }
      }

      return {}
    } catch {
      return { error: "An unexpected error occurred" }
    }
  }

  const devLogin = async (email?: string) => {
    if (!isDevLoginEnabled) {
      return { error: "Dev login is only available in local development" }
    }
    try {
      const result = await trpcVanilla.auth.devLogin.mutate(email ? { email } : undefined)
      const expiresAt = result?.session?.expires_at
      // Reject responses missing a valid token or finite expiry — otherwise a bad
      // session gets persisted and lingers (getDevSession can't expire it).
      if (!result?.session?.access_token || typeof expiresAt !== "number" || !Number.isFinite(expiresAt)) {
        return { error: "Dev login failed" }
      }

      const dev: DevSession = {
        user: result.user,
        accessToken: result.session.access_token,
        expiresAt,
      }
      setDevSession(dev)
      setSession(devSessionToSupabase(dev))
      setUser(toSupabaseUser(dev.user))
      return {}
    } catch (error) {
      return { error: error instanceof Error ? error.message : "Dev login failed" }
    }
  }

  const signOut = async () => {
    // Clear any local-dev session first so it doesn't re-hydrate on reload.
    clearDevSession()
    try {
      const { error } = await supabase.auth.signOut()

      if (error) {
        console.error("❌ Auth Context: Supabase signOut error:", error)
        // If the logout API call fails (403, network error, etc.),
        // we still want to clear the local session state
      }
    } catch (error) {
      console.error("❌ Auth Context: Unexpected signOut error:", error)
      // Clear local state even if API call fails
    } finally {
      // Always clear local state regardless of API response
      setSession(null)
      setUser(null)

      // Clear any persisted session data from localStorage
      try {
        localStorage.removeItem("supabase.auth.token")
        // Clear any other Supabase-related localStorage items
        Object.keys(localStorage).forEach((key) => {
          if (key.includes("supabase") || key.includes("sb-")) {
            localStorage.removeItem(key)
          }
        })
      } catch (localStorageError) {
        console.warn("⚠️  Auth Context: Could not clear localStorage:", localStorageError)
      }
      onSignedOut?.()
    }
  }

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      })

      if (error) {
        return { error: error.message }
      }

      return {}
    } catch {
      return { error: "An unexpected error occurred" }
    }
  }

  const updatePassword = async (password: string) => {
    try {
      const { error } = await supabase.auth.updateUser({ password })

      if (error) {
        return { error: error.message }
      }

      return {}
    } catch {
      return { error: "An unexpected error occurred" }
    }
  }

  const value = {
    user,
    session,
    loading,
    mfaRequired,
    currentAAL,
    nextAAL,
    signIn,
    signUp,
    signInWithGoogle,
    devLogin,
    isDevLoginEnabled,
    signOut,
    resetPassword,
    updatePassword,
    checkMFAStatus,
    mfaStatus,
    recheckMfa: checkMFAStatus,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
